import { SupportLanguages } from 'constant/supportLanguage'
import {
    CountryContract,
    CurrencyContract,
    CustomerLookupContract,
    KitchenCategorySortOrder,
    LogoutAfterPaymentOption,
    PosOperationalSettingContract,
} from 'models'
import { createSelector } from 'reselect'
import {
    getCurrencyActions,
    getOperationSettingActions,
    updateClosingProcedureSettingActions,
    updateGeneralSettingActions,
    updateOperationSettingActions,
    updateMenuConfigurationActions,
    getCompanyLogoActions,
    updateGeneralPOSTerminalActions,
    updateTimeClockModuleSettingActions,
    UPDATE_KITCHEN_CATEGORY_SORT_ORDER,
    updateGiftCardSalesSettingActions,
} from 'store/actions/operation-setting.action'
import { RootState } from './index.reducer'
import { Dictionary } from 'utilities/typeUtility/typeUtility'
type IData = Required<PosOperationalSettingContract>
interface IState {
    currency: CurrencyContract[]
    logo?: string
    currentCountry?: CountryContract
    loading: boolean
    data: IData
    error: any
    submitting: boolean
    defaultCustomersLoading: boolean
    defaultCustomers: CustomerLookupContract[]
    /**
     * Category Sort Order Index for feature related to kitchen. (e.g., kitchen's receipt, KDS)
     */
    kitchenCategorySortOrderMap: Dictionary<KitchenCategorySortOrder>
}
const defaultData: IData = {
    minPinLength: 6,
    currencyID: 2, // dollar
    currencyCode: 'USD',
    language: SupportLanguages[0].value,
    timeClock: true,
    tableLayout: true,
    tableSales: true,
    quickSales: true,
    onlineSales: true,
    orderDashboard: true,
    crm: true,
    reports: true,
    kds: false,
    loyalty: false,
    giftCard: false,
    customerFacingDisplay: false,

    closeAllOpenOrders: true,
    closeAllTerminals: true,
    closeOutAllEmployees: true,
    closePaymentSettlement: true,
    requireAllJournalHaveGLCodeAssigned: true,

    posTerminalUserIdleTimeout: '00:10:00',
    disableProductSelectionForOutOfStockItems: false,
    tableSalesRemoveDraftOrder: true,
    clearTableAfterCompletingAllBills: false,
    isAutoClearTable: false,
    enableGiftCardSales: false,
    currencySymbol: null,
    defaultCustomerCategoryID: '',
    operationalSettingId: '',
    generalServiceCharge: false,
    quickSalesServiceCharge: false,
    tableSalesServiceCharge: false,
    generalGlobalPOSDefaultCustomerID: '',
    quickServiceGlobalPOSDefaultCustomerID: '',
    tableServiceGlobalPOSDefaultCustomerID: '',
    strictEnforcementOfTimeAvailabilityonSales: true,
    allowToSearchAcrossAllMenus: false,
    showOnlineStoresOnQuickService: false,
    showBrandFilterOnQuickService: false,
    showTimeAvailabilityOverrideFilterOnQuickService: false,
    allowToUseOpenItemOnQuickService: true,
    showOnlineStoresOnTableService: false,
    showBrandFilterOnTableService: false,
    showTimeAvailabilityOverrideFilterOnTableService: false,
    allowToUseOpenItemOnTableService: true,
    useTipsOnQuickService: true,
    useTipsOnTableService: true,
    logoutAfterPaymentOnQuickService: LogoutAfterPaymentOption.Disable,
    logoutAfterPaymentOnTableService: LogoutAfterPaymentOption.Disable,
    disableProductSelectionForOutOfStockIngredients: false,
    isCalculateCompAfterDiscount: true,
    tableSalesTrackGuestCount: false,
    requireTableIdForTableService: true,
    tableSalesAllowBillSplitting: false,
    isActiveKitchenIntegrationAndEventInQS: false,
    isActiveKitchenIntegrationAndEventInTS: false,
    isKdsConfigurationActive: false,
    isEvoPaymentEnabledBySystem: false,
    isPayntPaymentEnabledBySystem: false,
    tenantIdentifierID: '',
}
const initialState: IState = {
    currency: [],
    loading: false,
    data: defaultData,
    error: null,
    submitting: false,
    logo: undefined,
    defaultCustomersLoading: false,
    defaultCustomers: [],
    kitchenCategorySortOrderMap: {},
}
export const calculationCompSettingSelect = createSelector(
    (state: RootState) => state.operation.data.isCalculateCompAfterDiscount,
    (setting) => setting ?? true,
)
export default function OperationalSetting(
    state: IState = initialState,
    action: {
        type: string
        payload: any
    },
): IState {
    let error: any = action.payload
    switch (action.type) {
        case UPDATE_KITCHEN_CATEGORY_SORT_ORDER: {
            return {
                ...state,
                kitchenCategorySortOrderMap: action.payload,
            }
        }
        case getOperationSettingActions.onPending:
            return {
                ...state,
                loading: true,
                error: null,
            }
        case getOperationSettingActions.onSuccess:
            return {
                ...state,
                loading: false,
                data: action.payload,
                logo: action.payload.logo,
                currentCountry: action.payload.currentCountry,
            }
        case getOperationSettingActions.onFailed:
            if (error?.response?.status === 404)
                return {
                    ...state,
                    loading: false,
                    data: defaultData,
                }
            else
                return {
                    ...state,
                    loading: false,
                    error: action.payload,
                }
        case updateOperationSettingActions.onPending:
            return {
                ...state,
                loading: true,
                error: null,
            }
        case updateOperationSettingActions.onSuccess:
            return {
                ...state,
                loading: false,
                data: action.payload,
            }
        case updateOperationSettingActions.onFailed:
            return {
                ...state,
                loading: false,
                error: action.payload,
            }
        case getCurrencyActions.onPending:
            return {
                ...state,
                loading: true,
                error: null,
            }
        case getCurrencyActions.onSuccess:
            return {
                ...state,
                loading: false,
                currency: action.payload,
            }
        case getCurrencyActions.onFailed:
            return {
                ...state,
                loading: false,
                error: action.payload,
            }

        case getCompanyLogoActions.onSuccess: {
            return {
                ...state,
                logo: action.payload,
            }
        }
        case updateTimeClockModuleSettingActions.onSuccess: {
            return {
                ...state,
                data: { ...state.data, timeClock: action.payload },
            }
        }
        case updateGeneralSettingActions.onPending: {
            return {
                ...state,
                submitting: true,
            }
        }
        case updateGeneralSettingActions.onSuccess: {
            return {
                ...state,
                data: { ...state.data, ...action.payload },
                submitting: false,
            }
        }
        case updateGeneralSettingActions.onFailed: {
            return {
                ...state,
                submitting: false,
            }
        }
        case updateClosingProcedureSettingActions.onPending: {
            return {
                ...state,
                submitting: true,
            }
        }
        case updateClosingProcedureSettingActions.onSuccess: {
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.payload,
                },
                submitting: false,
            }
        }
        case updateClosingProcedureSettingActions.onFailed: {
            return {
                ...state,
                submitting: false,
            }
        }
        case updateMenuConfigurationActions.onPending:
            return {
                ...state,
                submitting: true,
            }
        case updateMenuConfigurationActions.onSuccess:
            return {
                ...state,
                data: {
                    ...state.data,
                    allowToUseOpenItemOnQuickService:
                        action.payload.allowToUseOpenItemOnQuickService,
                    showBrandFilterOnQuickService:
                        action.payload.showBrandFilter,
                    showOnlineStoresOnQuickService:
                        action.payload.showOnlineStores,
                    showTimeAvailabilityOverrideFilterOnQuickService:
                        action.payload
                            .showTimeAvailabilityOverrideFilterOnQuickService,
                    allowToUseOpenItemOnTableService:
                        action.payload.allowToUseOpenItemOnTableService,
                    showBrandFilterOnTableService:
                        action.payload.showBrandFilter,
                    showOnlineStoresOnTableService:
                        action.payload.showOnlineStores,
                    showTimeAvailabilityOverrideFilterOnTableService:
                        action.payload
                            .showTimeAvailabilityOverrideFilterOnTableService,
                },
                submitting: false,
            }
        case updateMenuConfigurationActions.onFailed:
            return {
                ...state,
                submitting: false,
            }
        case updateGeneralPOSTerminalActions.onPending: {
            return {
                ...state,
                submitting: true,
            }
        }
        case updateGeneralPOSTerminalActions.onSuccess: {
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.payload,
                },
                submitting: false,
            }
        }
        case updateGeneralPOSTerminalActions.onFailed: {
            return {
                ...state,
                submitting: false,
            }
        }
        case updateGiftCardSalesSettingActions.onSuccess: {
            return {
                ...state,
                data: {
                    ...state.data,
                    enableGiftCardSales: action.payload.enableGiftCardSales,
                },
                submitting: false,
            }
        }

        default:
            return state
    }
}
